import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@mui/material';

import Page from "~/components/Page";
import { userStore } from "~/stores/user";
import { useTrans } from '~/hooks/useTrans';
import { apiGeProjectInvitationById, apiRejectProjectInvitation, apiConfirmProjectInvitation } from "~/providers/api";
import { IOutlet } from "~/interfaces";
import { IClientProjectInvitation } from '~/providers/client-interfaces';
import { getLink } from "~/providers/route";
import Loader from '~/components/Loader';
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import { getStrong } from "~/utils/utils";

import "./style.less";

interface IState {
	invitation: IClientProjectInvitation;
	loading: boolean;
	desc: string;
	errorMsg: string;
}

const INIT_STATE: IState = {
	invitation: null,
	loading: true,
	desc: "",
	errorMsg: "",
};

export default function ProjectInvitationPage() {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>(INIT_STATE);
	const { params }: IOutlet = useOutletContext();
	const navigate = useNavigate();

	async function cancel() {
		const rejectData = await apiRejectProjectInvitation(state.invitation.id, lang);

		if (rejectData.noErrors) {
			// prejdeme na homepage
			navigate(getLink("projects"));
		} else {
			updateState({
				errorMsg: rejectData.errors.join(", "),
			});
		}
	}

	async function confirm() {
		const confirmData = await apiConfirmProjectInvitation(state.invitation.id, lang);

		if (confirmData.noErrors) {
			// prejdeme na detail projektu - nejsme interni
			navigate(getLink("project-keys", false, { projectId: state.invitation.projectId }));
		} else {
			updateState({
				errorMsg: confirmData.errors.join(", "),
			});
		}
	}

	async function loadData() {
		updateState(INIT_STATE);

		const invitationData = await apiGeProjectInvitationById(params.projectInvitationId, lang);
		const email = userStore.getState().user.email;

		updateState({
			loading: false,
			...invitationData.noErrors
				? {
					invitation: invitationData.data,
					desc: trans("invitation.desc", {
						emailReceiver: getStrong(email),
						role: getStrong(invitationData.data.invitedRole),
						emailSender: getStrong(invitationData.data.invitedEmail),
						projectTitle: getStrong(invitationData.data.invitedProjectName),
					}),
				}
				: {},
			errorMsg: invitationData.errors.join(", "),
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	return <Page checkLogged={true} title={trans("invitation.title")} breadcrumbs={[trans("invitation.title")]} onLoad={onPageLoad} seoTitleTrans="seo.title.invitation.project">
		{ state.loading && <Loader msg={trans("loadingData")} /> }
		{ !state.loading && !state.errorMsg && <>
			<p dangerouslySetInnerHTML={{ __html: state.desc }} className="projectInvitationPage__desc" />
			<div className="projectInvitationPage__buttons">
				<Button variant="outlined" color="success" onClick={cancel}>
					{ trans("invitation.btnCancel") }
				</Button>
				<Button variant="contained" color="success" onClick={confirm}>
					{ trans("invitation.btnAgree") }
				</Button>
			</div>
		</> }
		{ state.errorMsg && <div>
			<ErrorInfo msg={state.errorMsg} className="margin-top-large" />
		</div> }
	</Page>;
}
